import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ActivityTrackSample } from "../../types/trail";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

interface ActivityMetricChartsProps {
  samples?: ActivityTrackSample[];
}

function formatElapsed(value: number): string {
  const minutes = Math.floor(value);
  const seconds = Math.round((value - minutes) * 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function ActivityMetricCharts({ samples = [] }: ActivityMetricChartsProps) {
  const data = useMemo(() => {
    const startTime = samples.reduce<number | undefined>((earliest, sample) => {
      if (!sample.timestamp) return earliest;
      const time = Date.parse(sample.timestamp);
      return Number.isFinite(time) && (earliest === undefined || time < earliest) ? time : earliest;
    }, undefined);
    if (startTime === undefined) return [];

    return samples.flatMap((sample) => {
      if (!sample.timestamp) return [];
      const time = Date.parse(sample.timestamp);
      if (!Number.isFinite(time)) return [];
      return [{
        elapsed: (time - startTime) / 60000,
        pace: sample.pace,
        heartRate: sample.heartRate,
      }];
    });
  }, [samples]);

  const paceData = data.filter((sample) => sample.pace !== undefined && sample.pace > 0);
  const heartRateData = data.filter((sample) => sample.heartRate !== undefined);
  if (!paceData.length && !heartRateData.length) return null;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {paceData.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Allure</CardTitle></CardHeader>
          <CardContent>
            <div className="h-56" aria-label="Graphique d'allure">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={paceData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="elapsed" tickFormatter={formatElapsed} minTickGap={24} />
                  <YAxis reversed domain={["dataMin", "dataMax"]} width={48} tickFormatter={(value: number) => `${value.toFixed(1)}`} />
                  <Tooltip labelFormatter={(value) => `Temps ${formatElapsed(Number(value))}`} formatter={(value) => [`${Number(value).toFixed(1)} min/km`, "Allure"]} />
                  <Line type="monotone" dataKey="pace" name="Allure" stroke="#ea580c" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      {heartRateData.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Fréquence cardiaque</CardTitle></CardHeader>
          <CardContent>
            <div className="h-56" aria-label="Graphique de fréquence cardiaque">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={heartRateData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="elapsed" tickFormatter={formatElapsed} minTickGap={24} />
                  <YAxis width={48} />
                  <Tooltip labelFormatter={(value) => `Temps ${formatElapsed(Number(value))}`} formatter={(value) => [`${value} bpm`, "Fréquence cardiaque"]} />
                  <Line type="monotone" dataKey="heartRate" name="Fréquence cardiaque" stroke="#dc2626" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
